import { PropsWithChildren } from 'react';
import { APIFieldAttributes, PersistedFieldQuestion } from '@ardoq/api-types';
import { useEffectOnce } from '@ardoq/hooks';
import { FlexBox } from '@ardoq/layout';
import { t } from '@ardoq/i18n';
import { getFieldOrAttributeQuestionUserFeedback } from '../getUserFeedback';
import { SurveyErrorMessage } from '../surveyQuestionAtoms';
import { SurveyFieldControl } from './SurveyFieldControl';
import { SharedFieldAndAttributeQuestionProps } from '../questionsTypes';
import { FieldAnswer, FieldAnswerValueWithoutDateRange } from '../../types';

export type SurveyFieldQuestionProps = SharedFieldAndAttributeQuestionProps & {
  originalValue: FieldAnswerValueWithoutDateRange | undefined;
  value: FieldAnswerValueWithoutDateRange | undefined;
  question: PersistedFieldQuestion;
  answer: FieldAnswer<FieldAnswerValueWithoutDateRange> | undefined;
  field: APIFieldAttributes | undefined;
};

export const SurveyFieldQuestion = ({
  surveyId,
  question,
  answer,
  readonly,
  value,
  originalValue,
  type,
  field,
  validateDuplicateName,
  isLocked,
  currentLocale,
  componentName,
  componentTypeName,
  componentId,
  children,
  isReference,
  hasAttemptedSubmission,
  hasAutocompleteFeature,
  isEmbedded,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  highlightUnansweredPriorityQuestions,
  i18nLocale,
  getExistingComponentByName,
  setAnswer,
  getDuplicate,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
  generateComponentDescription,
}: PropsWithChildren<SurveyFieldQuestionProps>) => {
  const questionId = question._id;
  const validators = question.validators;
  useEffectOnce(() => {
    if (value === undefined || originalValue !== undefined) return;
    setAnswer({
      questionId,
      value: field?.defaultValue as FieldAnswerValueWithoutDateRange,
    });
  });

  const isMandatory = Boolean(
    !isLocked &&
    validators &&
    validators.some(validator => validator.type === 'mandatory')
  );

  const fieldLabel = field ? field.label : '';

  const userFeedback = getFieldOrAttributeQuestionUserFeedback({
    isMandatory,
    value,
    type,
    fieldLabel,
    i18nLocale,
  });

  const readOnlyOrLocked = Boolean(readonly || isLocked);
  const noFieldTypeFoundMessage =
    type === undefined
      ? t(
          i18nLocale,
          'surveysUi:validation.fieldNotFound',
          'Field type not found. Was the field removed from the workspace?'
        )
      : null;

  return (
    <FlexBox align="flex-start" justify="flex-start">
      <QuestionContentWrapper
        question={question}
        answer={answer}
        highlightUnansweredPriorityQuestions={
          highlightUnansweredPriorityQuestions
        }
        i18nLocale={i18nLocale}
      >
        {type ? (
          <>
            <SurveyFieldControl
              userFeedback={userFeedback}
              type={type}
              readOnlyOrLocked={readOnlyOrLocked}
              surveyId={surveyId}
              questionId={questionId}
              setAnswer={setAnswer}
              field={field}
              currentLocale={currentLocale}
              i18nLocale={i18nLocale}
              originalValue={originalValue}
              value={value}
              validateDuplicateName={validateDuplicateName}
              getDuplicate={getDuplicate}
              componentName={componentName}
              componentTypeName={componentTypeName}
              componentId={componentId}
              isReference={isReference}
              isEmbedded={isEmbedded}
              hasAttemptedSubmission={hasAttemptedSubmission}
              hasAutocompleteFeature={hasAutocompleteFeature}
              hideReadOnlyLabel={hideReadOnlyLabel}
              questionLabel={fieldLabel}
              getExistingComponentByName={getExistingComponentByName}
              parentName={undefined}
              getAISuggestions={getAISuggestions}
              fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
              userOptions={userOptions}
              generateComponentDescription={generateComponentDescription}
            />
          </>
        ) : (
          noFieldTypeFoundMessage && (
            <SurveyErrorMessage>{noFieldTypeFoundMessage}</SurveyErrorMessage>
          )
        )}
        {children}
      </QuestionContentWrapper>
    </FlexBox>
  );
};
